import type { DeIdentificationCandidate } from '../../../core/entities/ai-assist-config';
import { useLocale } from '../i18n/LocaleProvider';

export function DeidentificationCandidateList({
  candidates,
  readonly,
  humanOnly,
  onHumanOnlyChange,
  onCandidateToggle,
  onCandidateReplacementChange,
}: {
  candidates: DeIdentificationCandidate[];
  readonly: boolean;
  humanOnly: boolean;
  onHumanOnlyChange: (humanOnly: boolean) => void;
  onCandidateToggle: (candidateId: string, enabled: boolean) => void;
  onCandidateReplacementChange: (candidateId: string, replacementValue: string) => void;
}) {
  const { t } = useLocale();
  const visibleCandidates = humanOnly ? candidates.filter((candidate) => candidate.sortBucket === 'human') : candidates;

  return (
    <section aria-label={t('settings.deidentification.candidates')} className="panel-card">
      <div className="candidate-toolbar">
        <div className="workspace-title-group">
          <p className="field-label">{t('settings.deidentification.candidates')}</p>
          <p className="notice-text">{humanOnly ? `${visibleCandidates.length}/${candidates.length}` : `${candidates.length}`}</p>
        </div>
        <button aria-pressed={humanOnly} className="hud-chip" onClick={() => onHumanOnlyChange(!humanOnly)} type="button">
          {humanOnly ? t('settings.deidentification.showAll') : t('settings.deidentification.showHumans')}
        </button>
      </div>
      <ul className="candidate-list">
        {visibleCandidates.map((candidate) => (
          <li key={candidate.candidateId} className="candidate-item">
            <div className="candidate-summary">
              <div className="candidate-title-row">
                <label className="candidate-toggle">
                  <input
                    aria-label={`${candidate.originalValue} ${t(`settings.deidentification.bucket.${candidate.sortBucket}` as never)}`}
                    checked={candidate.enabled}
                    disabled={readonly}
                    onChange={(event) => onCandidateToggle(candidate.candidateId, event.currentTarget.checked)}
                    type="checkbox"
                  />
                  <span className="candidate-name">{candidate.originalValue}</span>
                </label>
                <span className="candidate-type">{t(`settings.deidentification.bucket.${candidate.sortBucket}` as never)}</span>
              </div>
              <div className="candidate-occurrences">
                {candidate.occurrences.map((occurrence) => occurrence.displayLabel).join(' · ')}
              </div>
            </div>
            <div className="field-stack">
              <span className="field-label">{candidate.defaultReplacementValue ?? t('settings.deidentification.replacementPlaceholder')}</span>
              <input
                aria-label={`${candidate.originalValue} replacement`}
                className="workspace-input"
                disabled={readonly}
                onChange={(event) => onCandidateReplacementChange(candidate.candidateId, event.currentTarget.value)}
                placeholder={candidate.defaultReplacementValue ?? t('settings.deidentification.replacementPlaceholder')}
                type="text"
                value={candidate.replacementValue ?? ''}
              />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
